import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Cheap email regex — has @ and a TLD-like dot-suffix. Same shape we validate
// server-side in workflow 12.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ChipSuggestion {
  email: string;
  full_name?: string | null;
  is_existing_client?: boolean | null;
}

interface Props {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Inline label that appears at the very start of the row, e.g. "To:". */
  label?: string;
  /** Right-side action(s) — typically Add CC / Add BCC links. */
  rightAction?: React.ReactNode;
  /** Auto-focus the input when the field first mounts. */
  autoFocus?: boolean;
  /** Fires on every keystroke in the draft input. Wire this to a debounced
   *  contacts query and feed the result back in as `suggestions` — the
   *  field itself has no opinion on where matches come from. */
  onDraftChange?: (text: string) => void;
  /** Contacts to offer in a dropdown under the field while it's focused.
   *  Omit (or pass an empty array) to get the plain chip input with no
   *  autocomplete — every existing caller keeps working unchanged. */
  suggestions?: ChipSuggestion[];
}

// Email-chip multi-input. Type an address + Enter / Comma / Tab → chip.
// Backspace on empty input removes the last chip. Pasting a comma/space-separated
// list bulk-creates chips (handy for Reply All from copy-pasted recipient lists).
export function EmailChipsInput({
  values,
  onChange,
  placeholder = 'name@example.com',
  label,
  rightAction,
  autoFocus,
  onDraftChange,
  suggestions = [],
}: Props) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Dropdown visibility is separate from `suggestions` itself: `focused`
  // tracks real DOM focus, `dismissed` tracks an explicit Escape (cleared
  // again the moment the user types, like Gmail's own To-field picker).
  // Without `dismissed`, selecting a suggestion could flash the OLD list
  // back open for the ~150ms the parent's debounce takes to catch up to
  // the now-cleared draft.
  const [activeIdx, setActiveIdx] = useState(0);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const showSuggestions = focused && !dismissed && suggestions.length > 0;

  useEffect(() => {
    setActiveIdx(0);
  }, [suggestions]);

  function selectSuggestion(s: ChipSuggestion) {
    if (!values.includes(s.email)) onChange([...values, s.email]);
    setDraft('');
    onDraftChange?.('');
    setDismissed(true);
  }

  function commit(text: string) {
    const candidates = text
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    if (candidates.length === 0) return;
    const next = [...values];
    let added = false;
    for (const c of candidates) {
      if (!EMAIL_RE.test(c)) continue;
      if (next.includes(c)) continue;
      next.push(c);
      added = true;
    }
    if (added) onChange(next);
    setDraft('');
  }

  function remove(idx: number) {
    onChange(values.filter((_, i) => i !== idx));
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (showSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(suggestions.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        selectSuggestion(suggestions[activeIdx]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setDismissed(true);
        return;
      }
    }
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      if (draft.trim().length === 0) {
        // Tab without text: don't intercept — let the field focus advance.
        if (e.key === 'Tab') return;
        e.preventDefault();
        return;
      }
      e.preventDefault();
      commit(draft);
      return;
    }
    if (e.key === 'Backspace' && draft.length === 0 && values.length > 0) {
      e.preventDefault();
      remove(values.length - 1);
      return;
    }
  }

  return (
    <div
      className={cn(
        'relative flex w-full flex-wrap items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 transition-colors',
        'focus-within:border-foreground/40 focus-within:ring-1 focus-within:ring-foreground/15',
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {label ? (
        <span className="select-none pl-0.5 pr-0.5 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      ) : null}
      {values.map((v, i) => (
        <span
          key={`${v}-${i}`}
          className="inline-flex h-6 cursor-default items-center gap-1 rounded-full bg-secondary px-2 text-2xs font-medium text-secondary-foreground"
        >
          <span className="max-w-[16rem] truncate">{v}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              remove(i);
            }}
            className="cursor-pointer rounded-full text-muted-foreground hover:text-foreground"
            aria-label={`Remove ${v}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="email"
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          setDismissed(false);
          onDraftChange?.(v);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          commit(draft);
        }}
        onPaste={(e) => {
          const text = e.clipboardData.getData('text');
          if (/[\s,;]/.test(text)) {
            e.preventDefault();
            commit(draft + ' ' + text);
          }
        }}
        placeholder={values.length === 0 ? placeholder : ''}
        autoFocus={autoFocus}
        className="min-w-[120px] flex-1 border-0 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
      />
      {rightAction}
      {showSuggestions ? (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 flex flex-col overflow-hidden rounded-md border border-border bg-background shadow-xl">
          {suggestions.map((s, i) => (
            <button
              key={s.email}
              type="button"
              // Keeps focus on the draft input through the click — without
              // this, the input's onBlur fires first and commits/closes
              // before the click handler ever runs.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectSuggestion(s)}
              onMouseEnter={() => setActiveIdx(i)}
              className={cn(
                'flex items-center justify-between gap-2 px-2.5 py-1.5 text-left text-xs',
                i === activeIdx ? 'bg-accent' : 'hover:bg-accent/60',
              )}
            >
              <span className="min-w-0 flex-1 truncate">
                <span className="font-medium">{s.full_name ?? s.email}</span>
                {s.full_name ? <span className="ml-1 text-muted-foreground">· {s.email}</span> : null}
              </span>
              {s.is_existing_client ? (
                <span className="shrink-0 rounded bg-tint-success px-1.5 py-0.5 text-3xs font-medium text-tint-success-fg">
                  client
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
